import isUndefined from "lodash/isUndefined";
import { getObjectKeys } from "../objects.js";
const NUBMER_OF_DIGITS_AS_LARGE_NUMBER_PRICE = 6;
export const priceToNumber = (price) => {
    const { units, nanos } = price;
    return units + nanos / Math.pow(10, 9);
};
export const getEmptyBreakdown = (currencyCode = "") => {
    return {
        total: {
            currencyCode,
            nanos: 0,
            units: 0
        },
        baseFare: {
            currencyCode,
            nanos: 0,
            units: 0
        },
        fee: {
            currencyCode,
            nanos: 0,
            units: 0
        },
        tax: {
            currencyCode,
            nanos: 0,
            units: 0
        },
        discount: {
            currencyCode,
            nanos: 0,
            units: 0
        },
        totalWithoutDiscount: {
            currencyCode,
            nanos: 0,
            units: 0
        }
    };
};
export function sumPrice(a, b) {
    if (a.currencyCode !== b.currencyCode)
        throw new Error(`Incompatible currency codes: ${a.currencyCode} not equal to ${b.currencyCode}`);
    return normalizePrice({
        currencyCode: a.currencyCode,
        units: a.units + b.units,
        nanos: a.nanos + b.nanos
    });
}
export function sumTaxesAndFeesTotals(a, b) {
    const aKeys = Object.keys({ ...a });
    const bKeys = Object.keys({ ...b });
    if (!a || !aKeys.length)
        return b;
    if (!b || !bKeys.length)
        return a;
    const keys = [...new Set([...aKeys, ...bKeys])];
    return keys.reduce((summed, key) => ({
        ...summed,
        [key]: sumPrice(a[key], b[key])
    }), {});
}
export function sumTaxesAndFees(a, b) {
    const aKeys = Object.keys({ ...a });
    const bKeys = Object.keys({ ...b });
    if (!a || !aKeys.length)
        return b;
    if (!b || !bKeys.length)
        return a;
    const keys = [...new Set([...aKeys, ...bKeys])];
    return keys.reduce((summed, key) => {
        let taxesAndFeesTotals = {};
        if (a?.[key]?.taxesAndFeesTotals && b?.[key]?.taxesAndFeesTotals) {
            taxesAndFeesTotals = sumTaxesAndFeesTotals(a[key].taxesAndFeesTotals, b[key].taxesAndFeesTotals);
        }
        if (a?.[key]?.taxesAndFeesTotals && !b?.[key]?.taxesAndFeesTotals) {
            taxesAndFeesTotals = a[key].taxesAndFeesTotals;
        }
        if (!a?.[key]?.taxesAndFeesTotals && b?.[key]?.taxesAndFeesTotals) {
            taxesAndFeesTotals = b[key].taxesAndFeesTotals;
        }
        if (Object.keys(taxesAndFeesTotals).length) {
            summed[key] = { taxesAndFeesTotals };
        }
        return summed;
    }, {});
}
export function subtractPrice(a, b) {
    if (a.currencyCode !== b.currencyCode)
        throw new Error(`Incompatible currency codes: ${a.currencyCode} not equal to ${b.currencyCode}`);
    return normalizePrice({
        currencyCode: a.currencyCode,
        units: a.units - b.units,
        nanos: a.nanos - b.nanos
    });
}
export const sumBcomPricingItems = (a, b) => {
    const itemTypes = [...new Set([...a.map(({ itemType }) => itemType), ...b.map(({ itemType }) => itemType)])];
    return itemTypes.reduce((acc, itemType) => {
        const aItem = a.find((item) => item.itemType === itemType);
        const bItem = b.find((item) => item.itemType === itemType);
        const item = aItem || bItem;
        if (!item) {
            return acc;
        }
        const amount = aItem && bItem ? sumPrice(aItem.amount, bItem.amount) : aItem ? aItem.amount : bItem?.amount;
        if (amount)
            acc.push({ ...item, amount });
        return acc;
    }, []);
};
export const sumPriceBreakdown = (prices) => {
    if (prices.length == 0) {
        throw new Error("No input");
    }
    const currencyCode = prices[0].baseFare.currencyCode;
    const isTypeUIPrice = (input) => {
        return (!!input &&
            typeof input === "object" &&
            input.nanos !== undefined &&
            input.units !== undefined);
    };
    const sumUICarrierPriceBreakdown = (a, b) => {
        const result = {
            marketingCarrier: b.marketingCarrier,
            marketingCarrierName: b.marketingCarrierName,
            baseFare: sumPrice(a.baseFare, b.baseFare)
        };
        if (a.tax && b.tax)
            result.tax = sumPrice(a.tax, b.tax);
        if (a.tax && !b.tax)
            result.tax = a.tax;
        if (!a.tax && b.tax)
            result.tax = b.tax;
        if (a.taxesAndFees && b.taxesAndFees)
            result.taxesAndFees = sumTaxesAndFees(a.taxesAndFees, b.taxesAndFees);
        if (a.taxesAndFees && !b.taxesAndFees)
            result.taxesAndFees = a.taxesAndFees;
        if (!a.taxesAndFees && b.taxesAndFees)
            result.taxesAndFees = b.taxesAndFees;
        return result;
    };
    const setUICarrierPriceBreakdownValue = (price, value) => {
        const carrierPriceBreakdown = price.carrierPriceBreakdown || [];
        const list = [...carrierPriceBreakdown, ...value];
        const grouped = list.reduce((acc, curr) => {
            if (!acc[curr.marketingCarrier]) {
                acc[curr.marketingCarrier] = curr;
            }
            else {
                acc[curr.marketingCarrier] = sumUICarrierPriceBreakdown(acc[curr.marketingCarrier], curr);
            }
            return acc;
        }, {});
        price.carrierPriceBreakdown = Object.values(grouped) || [];
    };
    const isTypeUIBcomPricingItem = (input) => {
        return (!!input &&
            typeof input === "object" &&
            input.name !== undefined &&
            isTypeUIPrice(input.amount));
    };
    const isTypeUIBcomPricingItems = (input) => {
        return !!input && Array.isArray(input) && input.every((item) => isTypeUIBcomPricingItem(item));
    };
    const isTypeUICarrierPriceBreakdown = (input) => {
        if (!input)
            return false;
        if (!Array.isArray(input))
            return false;
        if (!input.length)
            return false;
        if (!input?.[0]?.marketingCarrier)
            return false;
        if (!input?.[0]?.marketingCarrierName)
            return false;
        if (!input?.[0]?.baseFare)
            return false;
        return true;
    };
    const isTypeBoolean = (input) => {
        return typeof input === "boolean";
    };
    const setUIPriceBreakdownValue = (priceBreakdown, key, value) => {
        if (key === "carrierPriceBreakdown" && isTypeUICarrierPriceBreakdown(value)) {
            setUICarrierPriceBreakdownValue(priceBreakdown, value);
        }
        else {
            priceBreakdown[key] = value;
        }
    };
    const output = getEmptyBreakdown(currencyCode);
    const summedPrices = prices.reduce((acc, priceBreakdown) => {
        const keys = getObjectKeys(priceBreakdown);
        keys.forEach((key) => {
            const accValue = acc[key];
            const pbdValue = priceBreakdown[key];
            if (!(isUndefined(accValue) && isUndefined(pbdValue))) {
                if (isTypeUIBcomPricingItems(accValue) && isTypeUIBcomPricingItems(pbdValue)) {
                    setUIPriceBreakdownValue(acc, key, sumBcomPricingItems(accValue, pbdValue));
                }
                else if (isTypeBoolean(accValue) && isTypeBoolean(pbdValue)) {
                    setUIPriceBreakdownValue(acc, key, Boolean(accValue) || Boolean(pbdValue));
                }
                else if (isTypeUIPrice(accValue) && isTypeUIPrice(pbdValue)) {
                    setUIPriceBreakdownValue(acc, key, sumPrice(accValue, pbdValue));
                }
                else {
                    if (!isUndefined(pbdValue)) {
                        setUIPriceBreakdownValue(acc, key, pbdValue);
                    }
                }
            }
        });
        return acc;
    }, output);
    if (summedPrices.totalRounded) {
        summedPrices.totalRounded = roundPrice(summedPrices.total);
    }
    if (summedPrices.totalWithoutDiscountRounded && summedPrices.totalWithoutDiscount) {
        summedPrices.totalWithoutDiscountRounded = roundPrice(summedPrices.totalWithoutDiscount);
    }
    return summedPrices;
};
export const roundPrice = (price) => ({
    ...price,
    nanos: 0,
    units: price.nanos > 0 ? price.units + 1 : price.units
});
export const arePricesDifferent = (firstPrice, secondPrice) => {
    return (firstPrice.currencyCode !== secondPrice.currencyCode ||
        firstPrice.nanos !== secondPrice.nanos ||
        firstPrice.units !== secondPrice.units);
};
export const isPriceWithLargeNumber = (price) => price.units.toString().length > NUBMER_OF_DIGITS_AS_LARGE_NUMBER_PRICE;
export const isPriceZero = (price) => price.nanos === 0 && price.units === 0;
export const getZeroUIPrice = (currencyCode) => ({
    currencyCode,
    nanos: 0,
    units: 0
});
export const normalizePrice = (price) => {
    const output = { ...price };
    if (output.nanos <= -1e9 || output.nanos >= 1e9) {
        const carry = Math.floor(output.nanos / 1e9);
        output.units += carry;
        output.nanos -= carry * 1e9;
    }
    if (output.units < 0 && output.nanos > 0) {
        output.units += 1;
        output.nanos -= 1e9;
    }
    else if (output.units > 0 && output.nanos < 0) {
        output.units -= 1;
        output.nanos += 1e9;
    }
    return output;
};
